// client/src/context/TabContext.js
//
// Tab state for the in-app tab strip (ST-00088). Installing the app as a PWA
// removed the browser's tab strip, so middle-clicking a link spawned a whole new
// OS window; this holds the tab list that replaces it.
//
// Each tab owns its own MemoryRouter (see components/layout/TabHost.jsx), so page
// components inside a tab get their own location/search params and their own
// back/forward history. Shell components (sidebar, nav bar) live outside the tabs
// and read the active tab from here instead of from the enclosing router.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getLabelForRoute } from '../hooks/useLastVisitedRoute';
import { loadTabSession, saveTabSession } from '../utils/tabSession';

// Phase 0 ships the host invisibly — one tab, no strip, behaviour identical to
// the untabbed shell. Unset the flag to fall straight back to that shell.
export const TABS_ENABLED = import.meta.env.REACT_APP_TABS_ENABLED === 'true';

const TabContext = createContext(null);

const EMPTY_NAV = {
  canGoBack: false,
  canGoForward: false,
  goBack: () => {},
  goForward: () => {},
  currentLabel: '',
};

// How many tabs stay mounted at once. Beyond this the least recently used is
// discarded — its strip entry and URL survive, but its subtree unmounts and
// remounts fresh when you go back to it, the way Chrome discards tabs. Keeps
// memory and background work bounded no matter how many tabs pile up.
const MAX_LIVE_TABS = 8;

let idSeq = 0;
const makeId = () => `tab-${++idSeq}`;

function enforceLiveCap(tabs, protectedId) {
  const live = tabs.filter((t) => !t.discarded);
  if (live.length <= MAX_LIVE_TABS) return tabs;

  const victims = new Set(
    live
      .filter((t) => t.id !== protectedId)
      // Pinned tabs are the ones you deliberately keep to hand, so they go last.
      .sort((a, b) => (a.pinned === b.pinned ? a.lastActiveAt - b.lastActiveAt : a.pinned ? 1 : -1))
      .slice(0, live.length - MAX_LIVE_TABS)
      .map((t) => t.id)
  );
  return tabs.map((t) => (victims.has(t.id) ? { ...t, discarded: true } : t));
}

/** Pinned tabs sit at the front of the strip, keeping their relative order. */
function pinnedFirst(tabs) {
  const pinned = tabs.filter((t) => t.pinned);
  return pinned.length ? [...pinned, ...tabs.filter((t) => !t.pinned)] : tabs;
}

export function pathnameOf(url) {
  return url.split(/[?#]/)[0];
}

function currentUrl() {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

// The provider mounts as soon as the user is authenticated, which can happen
// while the address bar still reads /login. Seeding a tab from that would render
// the login screen inside a pane.
const AUTH_PATHS = new Set(['/login', '/forgot-password', '/set-password', '/unauthorized']);

function seedUrl() {
  const url = currentUrl();
  return AUTH_PATHS.has(pathnameOf(url)) ? '/' : url;
}

// getLabelForRoute has no entry for '/' and derives names from the path, so the
// dashboard comes back empty. A tab must always be nameable.
function labelFor(url) {
  return getLabelForRoute(pathnameOf(url)) || 'Operations Overview';
}

function makeTab(url, extra) {
  return {
    id: makeId(),
    url,
    label: labelFor(url),
    lastActiveAt: Date.now(),
    pinned: false,
    discarded: false,
    ...extra,
  };
}

/**
 * The tab set to open with: last session's, plus whatever the app was actually
 * launched on so a deep link or refresh still lands somewhere sensible.
 *
 * Restored tabs start discarded — they materialise when you focus one, so
 * relaunching with ten tabs doesn't mount ten pages.
 */
function buildInitialTabs() {
  const entry = seedUrl();
  const saved = loadTabSession();

  if (!saved) {
    const tab = makeTab(entry);
    return { tabs: [tab], activeId: tab.id };
  }

  const restored = saved.tabs.map((t) => makeTab(t.url, { pinned: t.pinned, discarded: true }));
  let activeIndex = Math.min(Math.max(saved.activeIndex, 0), restored.length - 1);

  if (entry !== '/') {
    const existing = restored.findIndex((t) => t.url === entry);
    if (existing !== -1) {
      activeIndex = existing;
    } else {
      restored.push(makeTab(entry, { discarded: true }));
      activeIndex = restored.length - 1;
    }
  }

  restored[activeIndex] = {
    ...restored[activeIndex],
    discarded: false,
    lastActiveAt: Date.now(),
  };
  return { tabs: pinnedFirst(restored), activeId: restored[activeIndex].id };
}

export function TabProvider({ children }) {
  const seedRef = useRef(null);
  if (!seedRef.current) seedRef.current = buildInitialTabs();

  const [tabs, setTabs] = useState(() => seedRef.current.tabs);
  const [activeId, setActiveId] = useState(() => seedRef.current.activeId);
  // tabId -> { canGoBack, canGoForward, goBack, goForward, currentLabel },
  // published by each tab's bridge so the shared NavigationBar can drive the
  // active tab's own history.
  const [navByTab, setNavByTab] = useState(() => ({}));

  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;

  // tabId -> that tab's react-router navigate(). Lets the popstate handler drive
  // a specific tab's MemoryRouter from outside it.
  const navigatorsRef = useRef(new Map());

  // Browser history is a flat chronological log of (tab, url) entries across all
  // tabs. We stamp each with a sequence number and keep those numbers in browser
  // order, so a popstate can tell a single step from a multi-entry jump.
  const seqRef = useRef(0);
  const orderRef = useRef([]);
  const cursorRef = useRef(-1);

  const registerNavigator = useCallback((tabId, navigate) => {
    navigatorsRef.current.set(tabId, navigate);
    return () => {
      navigatorsRef.current.delete(tabId);
    };
  }, []);

  const updateTabLocation = useCallback((tabId, url) => {
    setTabs((prev) =>
      prev.map((t) => (t.id === tabId && t.url !== url ? { ...t, url, label: labelFor(url) } : t))
    );
  }, []);

  const publishNav = useCallback((tabId, nav) => {
    setNavByTab((prev) => {
      const cur = prev[tabId];
      if (
        cur &&
        cur.canGoBack === nav.canGoBack &&
        cur.canGoForward === nav.canGoForward &&
        cur.currentLabel === nav.currentLabel &&
        cur.goBack === nav.goBack &&
        cur.goForward === nav.goForward
      )
        return prev;
      return { ...prev, [tabId]: nav };
    });
  }, []);

  /**
   * Point the address bar at the active tab, so deep links, refresh, bookmarks
   * and the mouse back button keep working. Idempotent — StrictMode
   * double-invokes the effect that calls this.
   */
  const syncAddressBar = useCallback((tabId, url) => {
    const state = window.history.state;
    if (state && state.lbTab === tabId && state.lbUrl === url) return;

    const seq = ++seqRef.current;
    const entry = { ...state, lbTab: tabId, lbUrl: url, lbSeq: seq };

    if (!state || !state.lbTab) {
      // First sync of the session: tag the entry the page loaded on rather than
      // stacking a duplicate on top of it.
      window.history.replaceState(entry, '', url);
      orderRef.current = [seq];
      cursorRef.current = 0;
      return;
    }
    window.history.pushState(entry, '', url);
    orderRef.current = [...orderRef.current.slice(0, cursorRef.current + 1), seq];
    cursorRef.current = orderRef.current.length - 1;
  }, []);

  // Navigate the active tab from shell code that sits outside the tab routers.
  const navigateActiveTab = useCallback((url) => {
    const navigate = navigatorsRef.current.get(activeIdRef.current);
    if (navigate) navigate(url);
  }, []);

  const activateTab = useCallback((tabId) => {
    setActiveId((prev) => (prev === tabId ? prev : tabId));
    // Focusing a discarded tab brings it back — it remounts from its stored URL.
    setTabs((prev) =>
      enforceLiveCap(
        prev.map((t) =>
          t.id === tabId ? { ...t, discarded: false, lastActiveAt: Date.now() } : t
        ),
        tabId
      )
    );
  }, []);

  const openTab = useCallback((url, { background = false } = {}) => {
    const tab = makeTab(url);
    setTabs((prev) =>
      enforceLiveCap(pinnedFirst([...prev, tab]), background ? activeIdRef.current : tab.id)
    );
    if (!background) setActiveId(tab.id);
    return tab.id;
  }, []);

  const reorderTab = useCallback((tabId, toIndex) => {
    setTabs((prev) => {
      const from = prev.findIndex((t) => t.id === tabId);
      if (from === -1 || from === toIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(Math.max(0, Math.min(toIndex, next.length)), 0, moved);
      // Dropping across the pin boundary shouldn't smuggle a tab out of its group.
      return pinnedFirst(next);
    });
  }, []);

  // Recently closed tabs, newest last — the Ctrl+Shift+T stack.
  const closedRef = useRef([]);
  // tabId -> warning text (or true), set by pages via useTabDirty. Closing a tab
  // unmounts its page for good, and unlike the tab itself that has no undo.
  const dirtyRef = useRef(new Map());

  const setTabDirtyFor = useCallback((tabId, value) => {
    if (value === null || value === undefined) dirtyRef.current.delete(tabId);
    else dirtyRef.current.set(tabId, value);
  }, []);

  const forgetTab = useCallback((tabId) => {
    dirtyRef.current.delete(tabId);
    navigatorsRef.current.delete(tabId);
    setNavByTab((p) => {
      if (!(tabId in p)) return p;
      const { [tabId]: _removed, ...rest } = p;
      return rest;
    });
  }, []);

  const closeTab = useCallback(
    (tabId) => {
      const prev = tabsRef.current;
      if (prev.length <= 1) return; // the last tab stays; there is nowhere to go
      const idx = prev.findIndex((t) => t.id === tabId);
      if (idx === -1) return;

      const dirty = dirtyRef.current.get(tabId);
      if (dirty) {
        const warning = typeof dirty === 'string' ? dirty : 'This tab has unsaved changes.';
        // eslint-disable-next-line no-alert
        if (!window.confirm(`${warning}\n\nClose this tab anyway?`)) return;
      }

      const closed = prev[idx];
      closedRef.current = [
        ...closedRef.current.slice(-9),
        { url: closed.url, pinned: closed.pinned, index: idx },
      ];

      const next = prev.filter((t) => t.id !== tabId);
      setTabs(next);
      if (activeIdRef.current === tabId) {
        // Focus the tab that slid into this slot, else the new last one.
        setActiveId((next[idx] || next[next.length - 1]).id);
      }
      forgetTab(tabId);
    },
    [forgetTab]
  );

  /** Close every tab except this one, keeping pinned tabs and unsaved work. */
  const closeOtherTabs = useCallback(
    (tabId) => {
      const kept = tabsRef.current.filter(
        (t) => t.id === tabId || t.pinned || dirtyRef.current.has(t.id)
      );
      if (kept.length === tabsRef.current.length) return;
      tabsRef.current.forEach((t) => {
        if (!kept.includes(t)) forgetTab(t.id);
      });
      setTabs(kept);
      setActiveId(tabId);
    },
    [forgetTab]
  );

  /** Close everything after this one, keeping pinned tabs and unsaved work. */
  const closeTabsToRight = useCallback(
    (tabId) => {
      const prev = tabsRef.current;
      const idx = prev.findIndex((t) => t.id === tabId);
      if (idx === -1) return;
      const kept = prev.filter((t, i) => i <= idx || t.pinned || dirtyRef.current.has(t.id));
      if (kept.length === prev.length) return;
      prev.forEach((t) => {
        if (!kept.includes(t)) forgetTab(t.id);
      });
      setTabs(kept);
      if (!kept.some((t) => t.id === activeIdRef.current)) setActiveId(tabId);
    },
    [forgetTab]
  );

  const duplicateTab = useCallback((tabId) => {
    const source = tabsRef.current.find((t) => t.id === tabId);
    if (!source) return null;
    const tab = makeTab(source.url);
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.id === tabId);
      const next = [...prev];
      next.splice(idx + 1, 0, tab);
      return enforceLiveCap(pinnedFirst(next), tab.id);
    });
    setActiveId(tab.id);
    return tab.id;
  }, []);

  const togglePin = useCallback((tabId) => {
    setTabs((prev) =>
      pinnedFirst(prev.map((t) => (t.id === tabId ? { ...t, pinned: !t.pinned } : t)))
    );
  }, []);

  const reopenClosedTab = useCallback(() => {
    const entry = closedRef.current[closedRef.current.length - 1];
    if (!entry) return null;
    closedRef.current = closedRef.current.slice(0, -1);

    const tab = makeTab(entry.url, { pinned: entry.pinned });
    setTabs((prev) => {
      const next = [...prev];
      next.splice(Math.min(entry.index, next.length), 0, tab);
      return enforceLiveCap(pinnedFirst(next), tab.id);
    });
    setActiveId(tab.id);
    return tab.id;
  }, []);

  // Remember the open set so relaunching lands back on it.
  useEffect(() => {
    saveTabSession({
      tabs,
      activeIndex: Math.max(
        0,
        tabs.findIndex((t) => t.id === activeId)
      ),
    });
  }, [tabs, activeId]);

  // Browser/mouse back and forward walk the flat log described above.
  useEffect(() => {
    function onPopState(event) {
      const navs = navigatorsRef.current;
      const state = event.state;

      if (!state || !state.lbTab || !navs.has(state.lbTab)) {
        // An entry we never tagged — a pre-tabs entry, or a navigation from
        // outside the app. Point the active tab at whatever the address bar says.
        const fallback = navs.get(activeIdRef.current);
        if (fallback) fallback(currentUrl(), { replace: true });
        return;
      }

      const navigate = navs.get(state.lbTab);
      const cursor = cursorRef.current;
      const target = orderRef.current.indexOf(state.lbSeq);
      if (target !== -1) cursorRef.current = target;

      // A single step inside the tab we're already on: walk that tab's own
      // history so its forward entries survive. Anything else — a multi-entry
      // jump, or an entry belonging to another tab — is re-pointed directly.
      if (state.lbTab === activeIdRef.current && target !== -1 && Math.abs(target - cursor) === 1) {
        navigate(target < cursor ? -1 : 1);
        return;
      }

      activateTab(state.lbTab);
      navigate(state.lbUrl, { replace: true });
    }

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [activateTab]);

  const activeTab = tabs.find((t) => t.id === activeId) || tabs[0];

  const value = useMemo(
    () => ({
      tabs,
      activeId,
      activeTab,
      activePathname: activeTab ? pathnameOf(activeTab.url) : '/',
      nav: navByTab[activeId] || EMPTY_NAV,
      openTab,
      closeTab,
      closeOtherTabs,
      closeTabsToRight,
      duplicateTab,
      togglePin,
      reopenClosedTab,
      setTabDirtyFor,
      reorderTab,
      activateTab,
      navigateActiveTab,
      registerNavigator,
      updateTabLocation,
      publishNav,
      syncAddressBar,
    }),
    [
      tabs,
      activeId,
      activeTab,
      navByTab,
      openTab,
      closeTab,
      closeOtherTabs,
      closeTabsToRight,
      duplicateTab,
      togglePin,
      reopenClosedTab,
      setTabDirtyFor,
      reorderTab,
      activateTab,
      navigateActiveTab,
      registerNavigator,
      updateTabLocation,
      publishNav,
      syncAddressBar,
    ]
  );

  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
}

export function useTabs() {
  const ctx = useContext(TabContext);
  if (!ctx) throw new Error('useTabs must be used inside a TabProvider');
  return ctx;
}

export default TabContext;
